import { startTransition, useCallback, useMemo, useState } from 'react';

import type { ItemMeta, RichTreeItem } from '../../types';
import {
  getAncestorTreeIds,
  getDataNodeIdFromTreeId,
  getParentTreeId,
  isRealNode,
  isRootTreeId
} from '../../utils/treeId';
import type { MoveNodeDialogProps } from '../components/MoveNodeDialog';
import { applyMovePreview } from '../utils/applyMovePreview';
import { getInitialParentDataNodeId } from '../utils/isMoveNodeStale';
import { isMoveTargetUnchanged } from '../utils/treeItem';
import { useIsMoveNodeStale } from './useIsMoveNodeStale';
import type { MoveDialogTarget } from './useRequestMove';

type MoveDialogState = {
  selectedParentId: string | null | undefined;
  expandedItems: string[];
};

type MoveMutationArgs = {
  nodeDataNodeId: string;
  oldParentDataNodeId: string | null;
  newParentDataNodeId: string | null;
};

type UseMoveDialogSessionArgs = {
  target: MoveDialogTarget;
  onClose: () => void;
  projectId: string;
  templateNodeId: string;
  proposalId?: string;
  treeItems: RichTreeItem[];
  itemMetaMap: Record<string, ItemMeta>;
  onShowMore: (parentId: string) => void;
  openParentIfNeeded: (treeId: string) => void;
  moveNodeMutation: (args: MoveMutationArgs) => Promise<unknown>;
  nodeIcon?: string;
  loadingParentIds?: Set<string>;
  hasMoreRoots?: boolean;
  isFetchingMoreRoots?: boolean;
  onLoadMoreRoots?: () => void;
};

function createMoveDialogState(target: MoveDialogTarget): MoveDialogState {
  return {
    selectedParentId: undefined,
    expandedItems: target.expandedAncestors
  };
}

export function useMoveDialogSession({
  target,
  onClose,
  projectId,
  templateNodeId,
  proposalId,
  treeItems,
  itemMetaMap,
  onShowMore,
  openParentIfNeeded,
  moveNodeMutation,
  nodeIcon,
  loadingParentIds,
  hasMoreRoots,
  isFetchingMoreRoots,
  onLoadMoreRoots
}: UseMoveDialogSessionArgs): MoveNodeDialogProps {
  const [state, setState] = useState<MoveDialogState>(() => createMoveDialogState(target));
  const [isSavingMove, setIsSavingMove] = useState(false);

  const moveNodeId = target.moveNodeId;
  const moveNodeMeta = target.moveNodeMeta;
  const selectedParentId = state.selectedParentId;

  const isMoveNodeStaleNow = useIsMoveNodeStale({
    projectId,
    templateNodeId,
    proposalId,
    nodeDataNodeId: target.moveNodeMeta.dataNodeId,
    initialParentDataNodeId: getInitialParentDataNodeId(target.moveNodeId),
    liveTreeItems: treeItems
  });
  const isStale = !isSavingMove && isMoveNodeStaleNow;

  const moveDialogItems = useMemo(() => {
    if (state.selectedParentId === undefined) return treeItems;
    return applyMovePreview(treeItems, moveNodeId, state.selectedParentId);
  }, [moveNodeId, state.selectedParentId, treeItems]);

  const currentParentTreeId = getParentTreeId(moveNodeId);
  const targetUnchanged = isMoveTargetUnchanged(moveNodeId, selectedParentId);

  const disabledParentTreeId = currentParentTreeId;
  const isMoveToRootDisabled = isRootTreeId(moveNodeId) && targetUnchanged;
  const canSaveMove = !targetUnchanged;

  const resolveMoveApiDataNodeId = useCallback(
    (treeId: string) => itemMetaMap[treeId]?.dataNodeId ?? getDataNodeIdFromTreeId(treeId),
    [itemMetaMap]
  );

  const close = useCallback(() => {
    setIsSavingMove(false);
    onClose();
  }, [onClose]);

  const selectParent = useCallback(
    (treeId: string) => {
      if (treeId === currentParentTreeId) return;

      const ancestorsToExpand = getAncestorTreeIds(treeId);
      ancestorsToExpand.filter((id) => id !== treeId).forEach(openParentIfNeeded);
      openParentIfNeeded(treeId);

      startTransition(() => {
        setState((prev) => ({
          ...prev,
          selectedParentId: treeId,
          expandedItems: [...new Set([...prev.expandedItems, ...ancestorsToExpand])]
        }));
      });
    },
    [currentParentTreeId, openParentIfNeeded]
  );

  const selectRoot = useCallback(() => {
    startTransition(() => {
      setState((prev) => ({ ...prev, selectedParentId: null }));
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (selectedParentId === undefined || isStale) return;

    const nodeDataNodeId = resolveMoveApiDataNodeId(moveNodeId);
    const oldParentDataNodeId = currentParentTreeId ? resolveMoveApiDataNodeId(currentParentTreeId) : null;
    const newParentDataNodeId = selectedParentId ? resolveMoveApiDataNodeId(selectedParentId) : null;

    setIsSavingMove(true);
    try {
      await moveNodeMutation({ nodeDataNodeId, oldParentDataNodeId, newParentDataNodeId });
      close();
    } catch {
      setIsSavingMove(false);
    }
  }, [close, currentParentTreeId, isStale, moveNodeId, moveNodeMutation, resolveMoveApiDataNodeId, selectedParentId]);

  const handleExpansionChange = useCallback(
    (_: React.SyntheticEvent | null, next: string[]) => {
      const prevSet = new Set(state.expandedItems);
      next.filter((id) => isRealNode(id) && !prevSet.has(id)).forEach(openParentIfNeeded);
      setState((prev) => ({ ...prev, expandedItems: next }));
    },
    [state.expandedItems, openParentIfNeeded]
  );

  return {
    open: true,
    onClose: close,
    onSave: handleSave,
    items: moveDialogItems,
    itemMetaMap,
    onShowMore,
    moveNodeMeta,
    nodeIcon,
    loadingParentIds,
    moveNodeId,
    selectedParentId,
    disabledParentTreeId,
    isMoveToRootDisabled,
    canSave: canSaveMove && !isStale,
    isStale,
    isSaving: isSavingMove,
    onSelectParent: selectParent,
    onSelectRoot: selectRoot,
    expandedItems: state.expandedItems,
    onExpandedItemsChange: handleExpansionChange,
    hasMoreRoots,
    isFetchingMoreRoots,
    onLoadMoreRoots
  };
}
